import { zodResolver } from "@hookform/resolvers/zod";
import { forwardRef, useEffect, useImperativeHandle, useMemo } from "react";
import { useForm } from "react-hook-form";
import { number, object, preprocess, string, infer as infer_ } from "zod";

import { NumberInputFormField } from "src/components/Form/NumberInputFormField";
import { TextAreaFormField } from "src/components/Form/TextAreaFormField";
import { TextInputFormField } from "src/components/Form/TextInputFormField";
import { CODE_REGEX } from "src/constants/common";
import {
  useGetScenarioCodePlaceholderExample,
  useGetScenarioNamePlaceholderExample,
} from "src/hooks/scenario";
import { useT } from "src/internationalization";
import { ShortScenario } from "src/types/scenario.ts";

export type CreateScenarioFormRef = {
  submit: (onSubmit: (data: ValidatedFormState) => void, onError: () => void) => () => void;
};

export type CreateScenarioFormProps = {
  scenarios: ShortScenario[];
  onValidChange: (isValid: boolean) => void;
};

type FormState = {
  name: string;
  code: string;
  threshold: string;
  description: string;
};

type ValidatedFormState = infer_<ReturnType<typeof useSchema>>;

export const CreateScenarioForm = forwardRef<CreateScenarioFormRef, CreateScenarioFormProps>(
  function CreateScenarioForm({ onValidChange, scenarios }, ref) {
    const t = useT();
    const validationSchema = useSchema(scenarios);
    const getNamePlaceholderExample = useGetScenarioNamePlaceholderExample();
    const getCodePlaceholderExample = useGetScenarioCodePlaceholderExample();

    const {
      control,
      handleSubmit,
      formState: { isValid, isSubmitted },
    } = useForm<FormState>({
      resolver: zodResolver(validationSchema),
      defaultValues: {
        name: "",
        code: "",
        threshold: "1",
        description: "",
      },
    });

    useEffect(() => {
      onValidChange(isSubmitted ? isValid : true);
      // We do not want to fire this effect when onValidChange changes
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isValid]);

    useImperativeHandle(ref, () => {
      return {
        submit: (onSubmit, onError) =>
          handleSubmit((data) => {
            const validatedData = data as unknown as ValidatedFormState;
            onSubmit(validatedData);
          }, onError),
      };
    });

    return (
      <div className="flex flex-col gap-7">
        <div className="text-textPrimary text-xl">
          {t("create.scenario.modal.identification.tab.title")}
        </div>
        <div className="flex flex-col gap-5">
          <TextInputFormField
            id="scenarioName"
            control={control}
            name="name"
            placeholder={t("general.form.placeholder.example", {
              example: getNamePlaceholderExample(),
            })}
            label={t("general.form.name.label")}
            helperText={t("scenarios.form.name.helper")}
          />
          <TextInputFormField
            id="scenarioCode"
            control={control}
            name="code"
            placeholder={t("general.form.placeholder.example", {
              example: getCodePlaceholderExample(),
            })}
            label={t("general.form.code.label")}
            helperText={t("scenarios.form.code.helper")}
          />
          <NumberInputFormField
            id="scenarioThreshold"
            control={control}
            name="threshold"
            min={1}
            placeholder={t("general.form.placeholder.example", { example: "100" })}
            label={t("scenarios.form.threshold.label")}
            helperText={t("scenarios.form.threshold.helper")}
          />
          <TextAreaFormField
            id="scenarioDescription"
            control={control}
            name="description"
            label={t("general.form.description.label")}
            helperText={t("scenarios.form.description.helper")}
          />
        </div>
      </div>
    );
  }
);

const useSchema = (scenarios: ShortScenario[]) => {
  const t = useT();

  return useMemo(() => {
    return object({
      name: string().min(1, t("general.form.name.error.required")),
      code: string()
        .min(1, t("general.form.code.error.required"))
        .regex(CODE_REGEX, {
          message: t("general.form.code.error.invalid"),
        })
        .refine(
          (value) => {
            return !scenarios.some((s) => s.code === value);
          },
          {
            message: t("general.form.code.error.unique"),
          }
        ),
      threshold: preprocess(
        (value) => {
          if (typeof value === "string") {
            const parsed = parseFloat(value);
            return isNaN(parsed) ? undefined : parsed;
          }
          return value;
        },
        number({
          message: t("scenarios.form.threshold.error.required"),
        }).min(0, t("scenarios.form.threshold.error.min", { min: 0 }))
      ),
      description: string().min(1, t("general.form.description.error.required")),
    });
  }, [scenarios, t]);
};
